import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { ArticleAutocomplete } from '../../components/ui/article-autocomplete';
import { 
  Wrench, Search, Package, MapPin, Plus, Minus, Trash2, 
  RotateCcw, Camera, FileText, CheckCircle, AlertTriangle,
  PenTool, ChevronRight, ChevronLeft, History, X, Upload,
  ClipboardList, TrendingDown, TrendingUp, Edit3
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const ADJUSTMENT_TYPES = [
  { 
    value: 'inventory_correction', 
    label: 'Corecție Inventar', 
    icon: Edit3, 
    color: 'bg-blue-100 text-blue-700 border-blue-300',
    description: 'Setează cantitatea exactă găsită la numărare'
  },
  { 
    value: 'unregistered_plus', 
    label: 'Plus Neînregistrat', 
    icon: TrendingUp, 
    color: 'bg-green-100 text-green-700 border-green-300',
    description: 'Adaugă cantitate (găsit în plus, transfer neinregistrat)'
  },
  { 
    value: 'unregistered_minus', 
    label: 'Minus Neînregistrat', 
    icon: TrendingDown, 
    color: 'bg-orange-100 text-orange-700 border-orange-300',
    description: 'Scade cantitate (consum neinregistrat, lipsă)'
  },
  { 
    value: 'scrap_loss', 
    label: 'Casare / Pierderi', 
    icon: Trash2, 
    color: 'bg-red-100 text-red-700 border-red-300',
    description: 'Elimină din stoc (deteriorat, expirat, pierdut)'
  },
  { 
    value: 'supplier_return', 
    label: 'Returnare Furnizor', 
    icon: RotateCcw, 
    color: 'bg-purple-100 text-purple-700 border-purple-300',
    description: 'Scade și marchează pentru returnare'
  },
];

const ADJUSTMENT_REASONS = [
  { value: 'inventariere_periodica', label: 'Inventariere periodică' },
  { value: 'inventariere_anuala', label: 'Inventariere anuală' },
  { value: 'diferenta_numarare', label: 'Diferență la numărare' },
  { value: 'deteriorare_transport', label: 'Deteriorare în transport' },
  { value: 'deteriorare_depozitare', label: 'Deteriorare la depozitare' },
  { value: 'produs_expirat', label: 'Produs expirat' },
  { value: 'produs_defect', label: 'Produs defect fabricație' },
  { value: 'furt_pierdere', label: 'Furt / Pierdere' },
  { value: 'consum_neinregistrat', label: 'Consum neinregistrat' },
  { value: 'transfer_neinregistrat', label: 'Transfer neinregistrat' },
  { value: 'eroare_receptie', label: 'Eroare la recepție' },
  { value: 'returnare_calitate', label: 'Returnare - probleme calitate' },
  { value: 'returnare_comanda_gresita', label: 'Returnare - comandă greșită' },
  { value: 'altele', label: 'Altele (specificați în note)' },
];

const StockAdjustmentPage = () => {
  const { articles: dataContextArticles, fetchArticles } = useData();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('new');
  
  // Wizard state
  const [step, setStep] = useState(1);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [stockEntries, setStockEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [addNewLocation, setAddNewLocation] = useState(false);
  const [newBinId, setNewBinId] = useState('');
  const [bins, setBins] = useState([]);
  
  // Adjustment details
  const [adjustmentType, setAdjustmentType] = useState('');
  const [quantityMode, setQuantityMode] = useState('set'); // 'set' or 'diff'
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  // Signature
  const [signature, setSignature] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef(null);
  
  // History
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Processing
  const [processing, setProcessing] = useState(false);

  // Fetch bins on mount
  useEffect(() => {
    fetchBins();
    fetchHistory();
    fetchArticles();
  }, [fetchArticles]);

  const fetchBins = async () => {
    try {
      const res = await api.get('/bins');
      setBins(res.data || []);
    } catch { setBins([]); }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get('/wms/stock/adjustments/history', { params: { limit: 50 } });
      setHistory(res.data || []);
    } catch { setHistory([]); }
    finally { setHistoryLoading(false); }
  };

  const fetchStockEntries = async (articleId) => {
    if (!articleId) return;
    try {
      const res = await api.get(`/wms/stock/by-article/${articleId}`);
      setStockEntries(res.data || []);
    } catch {
      setStockEntries([]);
    }
  };

  const handleArticleSelect = (article) => {
    setSelectedArticle(article);
    setSelectedEntry(null);
    setAddNewLocation(false);
    if (article?.id) {
      fetchStockEntries(article.id);
    } else {
      setStockEntries([]);
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setPhotoPreview(ev.target?.result);
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  // Canvas signature functions
  useEffect(() => {
    if (step === 4 && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [step]);

  const getTouchPos = (touch, canvas) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: (touch.clientX - rect.left) * (canvas.width / rect.width),
      y: (touch.clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = e.touches 
      ? getTouchPos(e.touches[0], canvas)
      : { x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY };
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = e.touches 
      ? getTouchPos(e.touches[0], canvas)
      : { x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY };
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing && canvasRef.current) {
      setIsDrawing(false);
      setSignature(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignature('');
    }
  };

  // Calculate new quantity
  const calculateNewQuantity = () => {
    const currentQty = selectedEntry?.quantity || 0;
    const inputQty = parseFloat(quantity) || 0;
    const type = ADJUSTMENT_TYPES.find(t => t.value === adjustmentType);
    
    if (adjustmentType === 'inventory_correction') {
      return inputQty; // Direct set
    } else if (adjustmentType === 'unregistered_plus') {
      return currentQty + inputQty;
    } else {
      // All minus types
      return currentQty - inputQty;
    }
  };

  const canProceed = () => {
    if (step === 1) return !!selectedArticle;
    if (step === 2) return !!(selectedEntry || (addNewLocation && newBinId));
    if (step === 3) return !!(adjustmentType && quantity && reason);
    if (step === 4) return !!signature;
    return false;
  };

  const nextStep = () => setStep(step + 1);
  const prevStep = () => setStep(step - 1);

  const resetWizard = () => {
    setStep(1);
    setSelectedArticle(null);
    setStockEntries([]);
    setSelectedEntry(null);
    setAddNewLocation(false);
    setNewBinId('');
    setAdjustmentType('');
    setQuantity('');
    setReason('');
    setNotes('');
    setPhotoFile(null);
    setPhotoPreview(null);
    setSignature('');
  };

  const handleSubmit = async () => {
    if (!signature) {
      toast.error('Semnătura este obligatorie');
      return;
    }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('article_id', selectedArticle.id);
      formData.append('article_name', selectedArticle.name);
      formData.append('stock_entry_id', selectedEntry?.id || '');
      formData.append('bin_id', selectedEntry?.bin_id || newBinId);
      formData.append('lot_number', selectedEntry?.lot_number || '');
      formData.append('adjustment_type', adjustmentType);
      formData.append('current_quantity', selectedEntry?.quantity || 0);
      formData.append('new_quantity', calculateNewQuantity());
      formData.append('reason', reason);
      formData.append('notes', notes);
      formData.append('signature', signature);
      
      if (photoFile) {
        formData.append('photo', photoFile);
      }
      
      const res = await api.post('/wms/stock/adjustment-complete', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(res.data.message || 'Ajustare înregistrată cu succes');
      resetWizard();
      fetchHistory();
      setActiveTab('history');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eroare la înregistrare ajustare');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-6 pb-20 lg:pb-6" data-testid="stock-adjustment-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 flex items-center gap-3">
            <Wrench className="w-9 h-9 text-amber-600" />
            Reglare Stoc
          </h1>
          <p className="text-base text-slate-600 mt-1">Ajustări, corecții și regularizări de stoc</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="new" className="gap-2">
            <Plus className="w-4 h-4" /> Ajustare Nouă
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="w-4 h-4" /> Istoric Ajustări
          </TabsTrigger>
        </TabsList>

        {/* NEW ADJUSTMENT TAB */}
        <TabsContent value="new" className="mt-6">
          <Card className="max-w-3xl mx-auto">
            <CardContent className="p-0">
              {/* Progress Steps */}
              <div className="px-6 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b">
                <div className="flex items-center justify-between max-w-lg mx-auto">
                  {[
                    { n: 1, label: 'Articol' },
                    { n: 2, label: 'Locație' },
                    { n: 3, label: 'Detalii' },
                    { n: 4, label: 'Confirmare' }
                  ].map((s, i) => (
                    <React.Fragment key={s.n}>
                      <div className="flex flex-col items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                          step === s.n ? 'bg-amber-600 text-white scale-110 shadow-lg' :
                          step > s.n ? 'bg-amber-200 text-amber-800' :
                          'bg-slate-200 text-slate-500'
                        }`}>
                          {step > s.n ? <CheckCircle className="w-5 h-5" /> : s.n}
                        </div>
                        <span className={`text-xs mt-1 ${step === s.n ? 'font-semibold text-amber-700' : 'text-slate-500'}`}>
                          {s.label}
                        </span>
                      </div>
                      {i < 3 && <div className={`flex-1 h-1 mx-2 rounded ${step > s.n ? 'bg-amber-300' : 'bg-slate-200'}`} />}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Step Content */}
              <div className="p-6 min-h-[400px]">
                {/* STEP 1: Article Selection */}
                {step === 1 && (
                  <div className="space-y-6" data-testid="step-1">
                    <div className="text-center mb-6">
                      <Package className="w-12 h-12 text-amber-500 mx-auto mb-2" />
                      <h2 className="text-xl font-bold text-slate-900">Selectați Articolul</h2>
                      <p className="text-slate-500">Căutați după nume, cod sau cod furnizor</p>
                    </div>

                    <div className="max-w-md mx-auto">
                      <ArticleAutocomplete
                        articles={dataContextArticles}
                        value={selectedArticle?.id || ''}
                        onSelect={handleArticleSelect}
                        placeholder="Căutați articol..."
                      />
                    </div>

                    {selectedArticle && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 max-w-md mx-auto mt-6 animate-in fade-in">
                        <div className="flex items-start gap-3">
                          <Package className="w-10 h-10 text-amber-600 flex-shrink-0" />
                          <div>
                            <p className="font-bold text-amber-900">{selectedArticle.name}</p>
                            <p className="text-sm text-amber-700">Cod: {selectedArticle.code || '-'}</p>
                            <p className="text-sm text-amber-700">Unitate: {selectedArticle.unit || 'buc'}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* STEP 2: Location Selection */}
                {step === 2 && (
                  <div className="space-y-6" data-testid="step-2">
                    <div className="text-center mb-6">
                      <MapPin className="w-12 h-12 text-amber-500 mx-auto mb-2" />
                      <h2 className="text-xl font-bold text-slate-900">Selectați Locația</h2>
                      <p className="text-slate-500">Alegeți BIN-ul și Lot-ul pentru ajustare</p>
                    </div>

                    {stockEntries.length > 0 ? (
                      <div className="space-y-3">
                        <Label className="text-sm font-medium">Locații cu stoc existent:</Label>
                        <div className="grid gap-2">
                          {stockEntries.map(entry => (
                            <div
                              key={entry.id}
                              onClick={() => { setSelectedEntry(entry); setAddNewLocation(false); }}
                              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                selectedEntry?.id === entry.id 
                                  ? 'border-amber-500 bg-amber-50 shadow-md' 
                                  : 'border-slate-200 hover:border-amber-300 hover:bg-amber-50/50'
                              }`}
                              data-testid={`stock-entry-${entry.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <MapPin className={`w-5 h-5 ${selectedEntry?.id === entry.id ? 'text-amber-600' : 'text-slate-400'}`} />
                                  <div>
                                    <p className="font-semibold">{entry.bin_code || entry.bin_id}</p>
                                    <p className="text-sm text-slate-500">Lot: {entry.lot_number || 'N/A'}</p>
                                  </div>
                                </div>
                                <Badge className={`text-lg px-3 py-1 ${selectedEntry?.id === entry.id ? 'bg-amber-600' : 'bg-slate-600'}`}>
                                  {entry.quantity} {entry.unit || 'buc'}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-slate-50 rounded-xl p-6 text-center">
                        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
                        <p className="text-slate-600">Acest articol nu are stoc înregistrat în niciun BIN.</p>
                        <p className="text-sm text-slate-500 mt-1">Puteți adăuga stoc într-o locație nouă.</p>
                      </div>
                    )}

                    {/* Add new location option */}
                    <div className="border-t pt-4 mt-4">
                      <div className="flex items-center gap-3 mb-3">
                        <Switch 
                          checked={addNewLocation} 
                          onCheckedChange={(v) => { setAddNewLocation(v); if(v) setSelectedEntry(null); }}
                        />
                        <Label>Adaugă în locație nouă</Label>
                      </div>
                      
                      {addNewLocation && (
                        <Select value={newBinId} onValueChange={setNewBinId}>
                          <SelectTrigger className="h-12">
                            <SelectValue placeholder="Selectați BIN-ul" />
                          </SelectTrigger>
                          <SelectContent>
                            {bins.map(b => (
                              <SelectItem key={b.id} value={b.id}>
                                {b.cabinet}/{b.shelf}/{b.position} ({b.code})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                )}

                {/* STEP 3: Adjustment Details */}
                {step === 3 && (
                  <div className="space-y-6" data-testid="step-3">
                    <div className="text-center mb-4">
                      <ClipboardList className="w-12 h-12 text-amber-500 mx-auto mb-2" />
                      <h2 className="text-xl font-bold text-slate-900">Detalii Ajustare</h2>
                    </div>

                    {/* Adjustment Type */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Tip Operațiune *</Label>
                      <div className="grid gap-2">
                        {ADJUSTMENT_TYPES.map(type => {
                          const Icon = type.icon;
                          return (
                            <div
                              key={type.value}
                              onClick={() => setAdjustmentType(type.value)}
                              className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                adjustmentType === type.value 
                                  ? `${type.color} border-current shadow-md` 
                                  : 'border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <Icon className="w-5 h-5" />
                                <div>
                                  <p className="font-semibold">{type.label}</p>
                                  <p className="text-xs opacity-80">{type.description}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Quantity */}
                    {adjustmentType && (
                      <div className="space-y-3 animate-in fade-in">
                        <Label className="text-sm font-medium">
                          {adjustmentType === 'inventory_correction' ? 'Cantitate Reală (găsită)' : 
                           adjustmentType === 'unregistered_plus' ? 'Cantitate de Adăugat' : 
                           'Cantitate de Scăzut'} *
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={quantity}
                          onChange={(e) => setQuantity(e.target.value)}
                          placeholder="Introduceți cantitatea"
                          className="h-12 text-lg"
                          data-testid="quantity-input"
                        />
                        
                        {/* Preview */}
                        {quantity && (
                          <div className="bg-slate-100 rounded-lg p-3 flex items-center justify-between">
                            <span className="text-slate-600">Stoc actual:</span>
                            <span className="font-bold">{selectedEntry?.quantity || 0} {selectedArticle?.unit || 'buc'}</span>
                          </div>
                        )}
                        {quantity && (
                          <div className={`rounded-lg p-3 flex items-center justify-between ${
                            calculateNewQuantity() < 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                          }`}>
                            <span>Stoc nou:</span>
                            <span className="font-bold text-lg">{calculateNewQuantity()} {selectedArticle?.unit || 'buc'}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Reason */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Motiv *</Label>
                      <Select value={reason} onValueChange={setReason}>
                        <SelectTrigger className="h-11">
                          <SelectValue placeholder="Selectați motivul" />
                        </SelectTrigger>
                        <SelectContent>
                          {ADJUSTMENT_REASONS.map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Notes */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Note / Detalii suplimentare</Label>
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Descrieți situația în detaliu..."
                        rows={3}
                      />
                    </div>

                    {/* Photo Upload */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Foto Dovadă (opțional)</Label>
                      {!photoPreview ? (
                        <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-amber-400 hover:bg-amber-50/50 transition-colors">
                          <Camera className="w-8 h-8 text-slate-400 mb-2" />
                          <span className="text-sm text-slate-500">Click pentru a adăuga foto</span>
                          <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                        </label>
                      ) : (
                        <div className="relative">
                          <img src={photoPreview} alt="Preview" className="w-full h-40 object-cover rounded-xl" />
                          <Button 
                            variant="destructive" 
                            size="sm" 
                            className="absolute top-2 right-2"
                            onClick={removePhoto}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* STEP 4: Confirmation & Signature */}
                {step === 4 && (
                  <div className="space-y-6" data-testid="step-4">
                    <div className="text-center mb-4">
                      <PenTool className="w-12 h-12 text-amber-500 mx-auto mb-2" />
                      <h2 className="text-xl font-bold text-slate-900">Confirmare & Semnătură</h2>
                      <p className="text-slate-500">Verificați și semnați pentru a finaliza</p>
                    </div>

                    {/* Summary */}
                    <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                      <h3 className="font-bold text-slate-900 border-b pb-2">Rezumat Ajustare</h3>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-slate-500">Articol:</span>
                        <span className="font-semibold">{selectedArticle?.name}</span>
                        
                        <span className="text-slate-500">Locație:</span>
                        <span className="font-semibold">{selectedEntry?.bin_code || bins.find(b => b.id === newBinId)?.code || '-'}</span>
                        
                        <span className="text-slate-500">Tip operațiune:</span>
                        <span className="font-semibold">{ADJUSTMENT_TYPES.find(t => t.value === adjustmentType)?.label}</span>
                        
                        <span className="text-slate-500">Stoc actual:</span>
                        <span className="font-semibold">{selectedEntry?.quantity || 0} {selectedArticle?.unit || 'buc'}</span>
                        
                        <span className="text-slate-500">Stoc nou:</span>
                        <span className={`font-bold text-lg ${calculateNewQuantity() < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {calculateNewQuantity()} {selectedArticle?.unit || 'buc'}
                        </span>
                        
                        <span className="text-slate-500">Motiv:</span>
                        <span className="font-semibold">{ADJUSTMENT_REASONS.find(r => r.value === reason)?.label}</span>
                      </div>
                      
                      {notes && (
                        <div className="pt-2 border-t">
                          <span className="text-slate-500 text-sm">Note:</span>
                          <p className="text-sm mt-1">{notes}</p>
                        </div>
                      )}
                      
                      {photoPreview && (
                        <div className="pt-2 border-t">
                          <span className="text-slate-500 text-sm">Foto atașată:</span>
                          <img src={photoPreview} alt="Dovadă" className="w-24 h-24 object-cover rounded-lg mt-1" />
                        </div>
                      )}
                    </div>

                    {/* Signature Canvas */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">Semnătura Responsabil *</Label>
                        <Button variant="ghost" size="sm" onClick={clearSignature} className="text-xs text-red-600 h-7">
                          Șterge
                        </Button>
                      </div>
                      
                      <div className="border-2 border-dashed border-slate-300 rounded-xl bg-white touch-none">
                        <canvas
                          ref={canvasRef}
                          width={400}
                          height={150}
                          className="w-full cursor-crosshair"
                          onMouseDown={startDrawing}
                          onMouseMove={draw}
                          onMouseUp={stopDrawing}
                          onMouseLeave={stopDrawing}
                          onTouchStart={startDrawing}
                          onTouchMove={draw}
                          onTouchEnd={stopDrawing}
                          data-testid="signature-canvas"
                        />
                      </div>
                      
                      <p className="text-xs text-slate-500 text-center">
                        Semnați cu degetul sau mouse-ul. Semnătura confirmă responsabilitatea pentru această ajustare.
                      </p>
                      
                      {!signature && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-center">
                          <p className="text-xs text-amber-700">Semnătura este obligatorie pentru a continua</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Navigation */}
              <div className="px-6 py-4 border-t bg-slate-50 flex items-center justify-between">
                <Button 
                  variant="outline" 
                  onClick={step === 1 ? resetWizard : prevStep}
                  className="h-11"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  {step === 1 ? 'Anulează' : 'Înapoi'}
                </Button>

                {step < 4 ? (
                  <Button 
                    onClick={nextStep} 
                    disabled={!canProceed()}
                    className="h-11 bg-amber-600 hover:bg-amber-700"
                  >
                    Continuă
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSubmit}
                    disabled={processing || !canProceed()}
                    className="h-11 bg-green-600 hover:bg-green-700"
                    data-testid="submit-adjustment"
                  >
                    {processing ? 'Se procesează...' : 'Confirmă Ajustare'}
                    <CheckCircle className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* HISTORY TAB */}
        <TabsContent value="history" className="mt-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <History className="w-6 h-6 text-amber-600" />
                  Istoric Ajustări
                </h2>
                <Button variant="outline" size="sm" onClick={fetchHistory}>
                  <RotateCcw className="w-4 h-4 mr-1" /> Reîmprospătează
                </Button>
              </div>

              {historyLoading ? (
                <div className="text-center py-12">
                  <RotateCcw className="w-8 h-8 animate-spin text-amber-600 mx-auto" />
                  <p className="text-slate-500 mt-2">Se încarcă...</p>
                </div>
              ) : history.length > 0 ? (
                <div className="space-y-3">
                  {history.map((item, idx) => {
                    const type = ADJUSTMENT_TYPES.find(t => t.value === item.adjustment_type);
                    const TypeIcon = type?.icon || FileText;
                    return (
                      <div key={item.id || idx} className="p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                        <div className="flex items-start gap-4">
                          <div className={`p-2 rounded-lg ${type?.color || 'bg-slate-200'}`}>
                            <TypeIcon className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-bold text-slate-900">{item.article_name}</p>
                                <p className="text-sm text-slate-500">{type?.label || item.adjustment_type}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold">
                                  {item.old_quantity} → {item.new_quantity}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {new Date(item.created_at).toLocaleString('ro-RO')}
                                </p>
                              </div>
                            </div>
                            <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
                              <span>BIN: {item.bin_code || item.bin_id}</span>
                              <span>Motiv: {ADJUSTMENT_REASONS.find(r => r.value === item.reason)?.label || item.reason}</span>
                              <span>De: {item.created_by_name}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Nicio ajustare înregistrată</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StockAdjustmentPage;
